/**
 * Copyright (c) Qwoach LLC 2024. All rights reserved.
 * Unauthorized copying of this file, via any medium is strictly prohibited
 * Proprietary and confidential
 * Created by Artem Bogush <artem@qwoach.com> on 16.01.24
 *
 */
import BaseObject from "@shared/BaseObject.js";
import {DataFilter} from "@qwoach/firebase-data-manager";
import {TraumaCoachingWorkflowItemId} from "@shared/Workflow/TraumaCoachingWorkflowItem.js";
import {asArray, getIndexKeyAnd} from "@/utils/helpers.js";

// let TraumaCoachingWorkflowItemId = "CbCaXgNEmoNqYa677cq0"

export default class TeamShareResourceManager extends BaseObject {


	constructor(dataManager) {
		super();
		this.dataManager = dataManager
		console.log("TeamShareResourceManager.constructor: dataManager = ", dataManager)
	}

	async memberShareClientResources(memberProfile, clientProfile){
		console.log("TeamShareResourceManager.memberShareClientResources", memberProfile, clientProfile)
		if(!memberProfile.hasPermissionClientScopeAll()){
			let teamMemberId = memberProfile.id
			let clientId = clientProfile.id

			await this.sharePurchasedPackages(teamMemberId, clientId)
			await this.shareJournalEntries(teamMemberId, clientId)
			await this.shareSignedLegalDocs(teamMemberId, clientId)
			await this.shareFiles(teamMemberId, clientId)
			await this.shareGenogram(teamMemberId, clientId)
			await this.shareTimeline(teamMemberId, clientId)
			await this.shareIAOTRCWorkflow(teamMemberId, clientId)
		}
	}
	async memberUnshareClientResourcesMultiple(teamMemberIds, clientId){
		let promiseArray = teamMemberIds.map(memberId => this.memberUnshareClientResources(memberId, clientId))
		return Promise.all(promiseArray)
	}

	async memberUnshareClientResources(teamMemberId, clientId){
		console.log("TeamShareResourceManager.memberUnshareClientResources", teamMemberId, clientId)
		await this.unsharePurchasedPackages(teamMemberId, clientId)
		await this.unshareJournalEntries(teamMemberId, clientId)
		await this.unshareSignedLegalDocs(teamMemberId, clientId)
		await this.unshareFiles(teamMemberId, clientId)
		await this.unshareGenogram(teamMemberId, clientId)
		await this.unshareTimeline(teamMemberId, clientId)
		await this.unshareIAOTRCWorkflow(teamMemberId, clientId)
	}

	async onShareableEntryCreated(userProfile, shareableEntry, clientProfile){
		console.log("TeamShareResourceManager.onShareableEntryCreated", userProfile, shareableEntry, clientProfile)
		let myIdForOwner = userProfile.getUserIdForOwner()
		shareableEntry.setCreatedBy(myIdForOwner, userProfile.name)
		await this.onEntrySharedWithClient(shareableEntry, clientProfile)
	}
	async onEntrySharedWithClient(shareableEntry, clientProfile){
		console.log("TeamShareResourceManager.onEntrySharedWithClient", shareableEntry, clientProfile)
		let assignedToIds = this.getSharedWithIdsFromClientProfile(clientProfile)
		shareableEntry.addSharedWithById(assignedToIds)
	}
	async onEntryUnsharedWithClient(shareableEntry, clientProfile){
		console.log("TeamShareResourceManager.onEntryUnsharedWithClient", shareableEntry, clientProfile)
		let assignedToIds = this.getSharedWithIdsFromClientProfile(clientProfile)
		shareableEntry.removeSharedWithById(assignedToIds)
	}

	getSharedWithIdsFromClientProfile(clientProfile){
		let assignedToIds = clientProfile.assignedTo.map(shortProfile => [shortProfile.id, getIndexKeyAnd(clientProfile.id, shortProfile.id)])
		assignedToIds = assignedToIds.reduce((res, arr) => res.concat(arr), [])
		return assignedToIds
	}


	async sharePurchasedPackages(teamMemberId, clientId){
		console.log("TeamShareResourceManager.sharePurchasedPackages", teamMemberId, clientId)
		let dataFilter = new DataFilter(100)
			.where("sharedWithIds", "array-contains", clientId)
		let params = {teamMemberId, dataFilter, get: this.getPurchasedPackagesPager, batchUpdate: this.savePurchasedPackages, shareWithIdsExtra: [getIndexKeyAnd(clientId, teamMemberId)]}
		return this._shareInternal(params)
	}
	async unsharePurchasedPackages(teamMemberId, clientId){
		console.log("TeamShareResourceManager.unsharePurchasedPackages", teamMemberId, clientId)
		let dataFilter = new DataFilter(100)
			.where("sharedWithIds", "array-contains", clientId)
		let params = {teamMemberId, dataFilter, get: this.getPurchasedPackagesPager, batchUpdate: this.savePurchasedPackages, shareWithIdsExtra: [getIndexKeyAnd(clientId, teamMemberId)]}
		return this._unshareInternal(params)
	}


	async shareJournalEntries(teamMemberId, clientId){
		console.log("TeamShareResourceManager.shareJournalEntries", teamMemberId, clientId)
		let dataFilter = new DataFilter(100)
			.where("clientId", "==", clientId)
		let params = {teamMemberId, dataFilter, get: this.getJournalEntriesPager, batchUpdate: this.saveJournalEntries}
		return this._shareInternal(params)
	}
	async unshareJournalEntries(teamMemberId, clientId){
		console.log("TeamShareResourceManager.unshareJournalEntries", teamMemberId, clientId)
		let dataFilter = new DataFilter(100)
			.where("clientId", "==", clientId)
		let params = {teamMemberId, dataFilter, get: this.getJournalEntriesPager, batchUpdate: this.saveJournalEntries}
		return this._unshareInternal(params)
	}



	async shareSignedLegalDocs(teamMemberId, clientId){
		console.log("TeamShareResourceManager.shareSignedLegalDocs", teamMemberId, clientId)
		let dataFilter = new DataFilter(100)
			.where("clientId", "==", clientId)
		let params = {teamMemberId, dataFilter, get: this.getSignedLegalDocsPager, batchUpdate: this.saveSignedLegalDocs}
		return this._shareInternal(params)
	}
	async unshareSignedLegalDocs(teamMemberId, clientId){
		console.log("TeamShareResourceManager.unshareSignedLegalDocs", teamMemberId, clientId)
		let dataFilter = new DataFilter(100)
			.where("clientId", "==", clientId)
		let params = {teamMemberId, dataFilter, get: this.getSignedLegalDocsPager, batchUpdate: this.saveSignedLegalDocs}
		return this._unshareInternal(params)
	}



	async shareFiles(teamMemberId, clientId){
		console.log("TeamShareResourceManager.shareFiles", teamMemberId, clientId)
		let dataFilter = new DataFilter(100)
			.where("sharedWithIds", "array-contains", clientId)
		let params = {teamMemberId, dataFilter, get: this.getFilesPager, batchUpdate: this.saveFiles, shareWithIdsExtra: [getIndexKeyAnd(clientId, teamMemberId)]}
		return this._shareInternal(params)
	}
	async unshareFiles(teamMemberId, clientId){
		console.log("TeamShareResourceManager.unshareFiles", teamMemberId, clientId)
		let dataFilter = new DataFilter(100)
			.where("sharedWithIds", "array-contains", clientId)
		let params = {teamMemberId, dataFilter, get: this.getFilesPager, batchUpdate: this.saveFiles, shareWithIdsExtra: [getIndexKeyAnd(clientId, teamMemberId)]}
		return this._unshareInternal(params)
	}



	async shareGenogram(teamMemberId, clientId){
		console.log("TeamShareResourceManager.shareGenogram", teamMemberId, clientId)
		let dataFilter = new DataFilter(100)
			.where("clientId", "==", clientId)
		let params = {teamMemberId, dataFilter, get: this.getGenogramPager, batchUpdate: this.saveGenograms}
		return this._shareInternal(params)
	}
	async unshareGenogram(teamMemberId, clientId){
		console.log("TeamShareResourceManager.unshareGenogram", teamMemberId, clientId)
		let dataFilter = new DataFilter(100)
			.where("clientId", "==", clientId)
		let params = {teamMemberId, dataFilter, get: this.getGenogramPager, batchUpdate: this.saveGenograms}
		return this._unshareInternal(params)
	}



	async shareTimeline(teamMemberId, clientId){
		console.log("TeamShareResourceManager.shareTimeline", teamMemberId, clientId)
		let dataFilter = new DataFilter(100)
			.where("clientId", "==", clientId)
		let params = {teamMemberId, dataFilter, get: this.getTimelinePager, batchUpdate: this.saveTimelines}
		return this._shareInternal(params)
	}
	async unshareTimeline(teamMemberId, clientId){
		console.log("TeamShareResourceManager.unshareTimeline", teamMemberId, clientId)
		let dataFilter = new DataFilter(100)
			.where("clientId", "==", clientId)
		let params = {teamMemberId, dataFilter, get: this.getTimelinePager, batchUpdate: this.saveTimelines}
		return this._unshareInternal(params)
	}



	async shareIAOTRCWorkflow(teamMemberId, clientId){
		console.log("TeamShareResourceManager.shareIAOTRCWorkflow", teamMemberId, clientId)
		let workflowId = TraumaCoachingWorkflowItemId
		let iaotrcWorkflowItem = await this.getWorkflowItem(workflowId)
		if(iaotrcWorkflowItem){
			iaotrcWorkflowItem.addSharedWithById(teamMemberId)
			await this.saveWorkflowItem(iaotrcWorkflowItem)
			let dataFilter = new DataFilter(100)
				.where("clientId", "==", clientId)
			let params = {teamMemberId, dataFilter, get: this.getWorkflowStateItemPager, batchUpdate: this.saveWorkflowStateItem, args: [workflowId]}
			return this._shareInternal(params)
		}
	}
	async unshareIAOTRCWorkflow(teamMemberId, clientId){
		console.log("TeamShareResourceManager.unshareIAOTRCWorkflow", teamMemberId, clientId)
		let workflowId = TraumaCoachingWorkflowItemId
		let iaotrcWorkflowItem = await this.getWorkflowItem(workflowId)
		if(iaotrcWorkflowItem){
			iaotrcWorkflowItem.removeSharedWithById(teamMemberId)
			await this.saveWorkflowItem(iaotrcWorkflowItem)
			let dataFilter = new DataFilter(100)
				.where("clientId", "==", clientId)
			let params = {teamMemberId, dataFilter, get: this.getWorkflowStateItemPager, batchUpdate: this.saveWorkflowStateItem, args: [workflowId]}
			return this._unshareInternal(params)
		}
	}






	async _shareInternal(options){
		let params = {...options, changeShareMethodName: "addSharedWithById"}
		return this._changeSharedInternal(params)
	}
	async _unshareInternal(options){
		let params = {...options, changeShareMethodName: "removeSharedWithById"}
		return this._changeSharedInternal(params)
	}

	async _changeSharedInternal({teamMemberId, dataFilter, get, batchUpdate, changeShareMethodName, args, shareWithIdsExtra}){
		args = args || []
		shareWithIdsExtra = shareWithIdsExtra || []
		console.log("TeamShareResourceManager._changeSharedInternal: arguments = ", ...arguments)
		let pager = await get.call(this, dataFilter, ...args)
		while(pager.hasMore){
			let entries = await pager.next()
			let res = false
			for(let entry of entries){
				let ids = [teamMemberId, ...shareWithIdsExtra]
				console.log("TeamShareResourceManager._changeSharedInternal: ids = ", ids)
				res |= entry[changeShareMethodName](ids)
			}
			if(res){
				await batchUpdate.call(this, entries, ...args)
			}
		}
	}



	async getJournalEntriesPager(dataFilter){
		console.log("TeamShareResourceManager.getJournalEntriesPager", dataFilter)
		return this.dataManager.getJournalEntryListPager(dataFilter)
	}
	async saveJournalEntries(entries){
		return this.dataManager.batchUpdateJournalEntry(entries)
	}

	async getPurchasedPackagesPager(dataFilter){
		console.log("TeamShareResourceManager.getPurchasedPackagesPager", dataFilter)
		return this.dataManager.getPurchasedCoachingPackageListPager(dataFilter)
	}
	async savePurchasedPackages(entries){
		return this.dataManager.batchUpdatePurchasedCoachingPackage(entries)
	}

	async getSignedLegalDocsPager(dataFilter){
		return this.dataManager.getContractListPager(dataFilter)
	}
	async saveSignedLegalDocs(entries){
		console.log("TeamShareResourceManager.saveSignedLegalDocs", entries, ", this.dataManager = ", this.dataManager)
		return this.dataManager.batchUpdateContract(entries)
	}

	async getFilesPager(dataFilter){
		return this.dataManager.getDriveFileListPager(dataFilter)
	}
	async saveFiles(entries){
		return this.dataManager.batchUpdateDriveFiles(entries)
	}

	async getGenogramPager(dataFilter){
		return this.dataManager.getGenogramItemListPager(dataFilter)
	}
	async saveGenograms(entries){
		return this.dataManager.batchUpdateGenogramItem(entries)
	}

	async getTimelinePager(dataFilter){
		return this.dataManager.getTimelineItemListPager(dataFilter)
	}
	async saveTimelines(entries){
		return this.dataManager.batchUpdateTimelineItem(entries)
	}

	async getWorkflowItem(itemId){
		return this.dataManager.getWorkflowItem(itemId)
	}
	async saveWorkflowItem(itemId){
		return this.dataManager.updateWorkflowItem(itemId)
	}

	async getWorkflowStateItemPager(dataFilter, workflowId){
		return this.dataManager.getWorkflowStateItemListPager(workflowId, dataFilter)
	}
	async saveWorkflowStateItem(entries, workflowId){
		return this.dataManager.batchUpdateWorkflowStateItem(workflowId, entries)
	}





	async updateMemberStatsClientsCount(shortMemberProfiles, valueDiff = 1){
		return this.updateMemberStats(shortMemberProfiles, "clientsCount", valueDiff)
	}
	async updateMemberStatsFilesCount(shortMemberProfiles, valueDiff = 1){
		return this.updateMemberStats(shortMemberProfiles, "filesCount", valueDiff)
	}
	async updateMemberStatsFormsCount(shortMemberProfiles, valueDiff = 1){
		return this.updateMemberStats(shortMemberProfiles, "formsCount", valueDiff)
	}
	async updateMemberStatsLegalDocsTemplateCount(shortMemberProfiles, valueDiff = 1){
		return this.updateMemberStats(shortMemberProfiles, "legalDocsTemplateCount", valueDiff)
	}
	async updateMemberStatsSignedLegalDocsCount(shortMemberProfiles, valueDiff = 1){
		return this.updateMemberStats(shortMemberProfiles, "signedLegalDocsCount", valueDiff)
	}
	async updateMemberStatsSessionTypesCount(shortMemberProfiles, valueDiff = 1){
		return this.updateMemberStats(shortMemberProfiles, "sessionTypesCount", valueDiff)
	}


	async updateMemberStats(shortMemberProfiles, statName, valueDiff){
		console.log("TeamAssignManager.updateMemberStats: shortMemberProfiles = ", shortMemberProfiles, ", statName = ", statName, ", valueDiff = ", valueDiff)
		if(!valueDiff){
			return
		}
		if(typeof shortMemberProfiles === "string"){
			shortMemberProfiles = {id: shortMemberProfiles}
		}

		shortMemberProfiles = asArray(shortMemberProfiles)

		let inc = this.dataManager.fieldValueIncrement(valueDiff)
		let memberProfilesDiff = shortMemberProfiles.map(shortProfile => ({id: shortProfile.id, ["assignStats." + statName]: inc}))
		try{
			await this.batchUpdateTeamMemberProfile(memberProfilesDiff)
		} catch (e) {
			console.warn("batchUpdateTeamMemberProfile thrown. Might indicate that this TeamMemberProfile was removed before")
			console.warn(e)
		}
	}

	async batchUpdateTeamMemberProfile(memberProfiles){
		return this.dataManager.batchUpdateTeamMemberProfile(memberProfiles)
	}

}
